import { loadInventoryLocationOptions } from '@/src/api/loadOptions'
import { inventoryAttributeAction } from '@api'
import { UoM } from '@data'
import useProductInventory from '@hook/useProductInventory'
import { yupResolver } from '@hookform/resolvers/yup'
import { PHARMACY_MODAL_TYPE, useModalStore } from '@modal'
import { productInventorySchema } from '@schema/product.schema'
import { useAppSelector } from '@store/hooks'
import { ProductInventoryAttributes } from '@type/inventory.type'
import { ProductAttributes } from '@type/product.type'
import { Divider, FormButton, FormInputField, FormStep, Input, Modal, SearchInput } from '@ui'
import { FormSelect, Select } from '@ui/SearchInput/Select'
import { capitalizeDrugName, getError } from '@util'
import { Box, Form, Text } from 'grommet'
import { memo, useEffect, useMemo, useState, type SyntheticEvent } from 'react'
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { FaArrowLeft } from 'react-icons/fa'
import { InferType } from 'yup'
import ProductSuccess from './ProductSucess'

type Props = {
  onClose: () => void
  defaultData?: ProductInventoryAttributes & Partial<ProductAttributes>
  id?: string
}

const ApiStrengthRow = memo(({ index }: { index: number }) => {
  const { control, formState } = useFormContext()
  const apiStrengthErrors = formState.errors.api_strengths as unknown as Record<number, any> | undefined
  const errors = apiStrengthErrors?.[index]
  const strengthError = errors?.strength?.message
  const unitError = errors?.unit?.message

  return (
    <Box gap="small" width={'100%'}>
      <Box align="center" direction="row" width={'100%'}>
        <Box align="center" direction="row" gap="small">
          <Controller
            control={control}
            name={`api_strengths.${index}.api_name`}
            render={({ field }) => (
              <div>
                <Select
                  borderRadius={'25px'}
                  className="w-[180px]"
                  getOptionValue={({ value }: any) => value}
                  isDisabled
                  onChange={(val: any) => {
                    field.onChange(val?.value)
                  }}
                  options={[]}
                  placeholder="API (e.g. Amoxicillin)"
                  value={{ label: field?.value || '', value: field?.value || '' }}
                />
              </div>
            )}
          />

          <Box
            align="center"
            border={{ color: strengthError || unitError ? '#de2c21' : '#999999', side: 'all' }}
            direction="row"
            round={'100px'}
            width={'100%'}
          >
            <Controller
              control={control}
              name={`api_strengths.${index}.strength`}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="e.g. 500"
                  style={{ border: '0', height: '100%', padding: '', width: '105px' }}
                  type="text"
                />
              )}
            />

            <Box className="borderless !items-center !border-none" direction="row">
              <div className="borderless h-8"></div>
              <Controller
                control={control}
                name={`api_strengths.${index}.unit`}
                render={({ field }) => (
                  <SearchInput
                    dropProps={{}}
                    onChange={val => field.onChange(val?.value || '')}
                    options={UoM}
                    placeholder="unit"
                    setValue={() => {}}
                    style={{ border: 'none', outline: 0, padding: '', textTransform: 'lowercase' }}
                    value={field?.value || ''}
                  />
                )}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {(strengthError || unitError) && (
        <Text size="small" style={{ color: '#de2c21', marginTop: '4px' }}>
          {strengthError || unitError}
        </Text>
      )}
    </Box>
  )
})

ApiStrengthRow.displayName = 'ApiStrengthRow'

const ApiStrengthsField = memo(() => {
  const { control } = useFormContext()
  const { fields, append } = useFieldArray({
    control,
    name: 'api_strengths'
  })

  useEffect(() => {
    if (fields?.length === 0) {
      append({ api_name: '', strength: '', unit: '' })
    }
  }, [fields.length, append])

  return (
    <Box gap="small">
      <Text style={{ fontSize: '14px', fontWeight: 500 }}>
        API Strengths <span style={{ color: '#de2c21' }}>*</span>
      </Text>
      {fields?.map((field, index) => (
        <ApiStrengthRow index={index} key={field?.id} />
      ))}
    </Box>
  )
})

ApiStrengthsField.displayName = 'ApiStrengthsField'

export const AddStock = ({ onClose, id, defaultData }: Props) => {
  const { user } = useAppSelector(state => state.user)
  const { openModal } = useModalStore()
  const drugName = capitalizeDrugName(
    defaultData?.drug_name || (defaultData ? `${defaultData.generic_name}(${defaultData.brand_name})` : ''),
    ''
  )
  const { unit_of_measurement, package: package_types } = useAppSelector(state => state.config)

  const inventoryUnitOptions = useMemo(
    () => (unit_of_measurement ?? []).map(unit => ({ label: unit, value: unit })),
    [unit_of_measurement]
  )

  const packagingOptions = useMemo(() => (package_types ?? []).map(p => ({ label: p, value: p })), [package_types])

  const parsedApiStrengths = useMemo(() => {
    if (defaultData?.strength) {
      const lastSpaceIndex = defaultData.strength.lastIndexOf(' ')
      if (lastSpaceIndex === -1) {
        const compactStrength = defaultData.strength.match(/^(.+?)([a-zA-Z%]+)$/)
        return [
          {
            api_name: defaultData?.generic_name || '',
            strength: compactStrength?.[1] || defaultData.strength,
            unit: compactStrength?.[2] || ''
          }
        ]
      }

      return [
        {
          api_name: defaultData?.generic_name || '',
          strength: defaultData.strength.substring(0, lastSpaceIndex).trim(),
          unit: defaultData.strength.substring(lastSpaceIndex + 1).trim()
        }
      ]
    }

    // Priority 1: Use the structured api_strengths array if it exists
    if (defaultData?.api_strengths && defaultData.api_strengths?.length > 0) {
      return defaultData.api_strengths.map(item => {
        const strengthStr = item.strength || ''
        const lastSpaceIndex = strengthStr.lastIndexOf(' ')
        if (lastSpaceIndex === -1) {
          return { api_name: item.api_name, strength: strengthStr, unit: '' }
        }
        const strength = strengthStr.substring(0, lastSpaceIndex).trim()
        const unit = strengthStr.substring(lastSpaceIndex + 1).trim()
        return { api_name: item.api_name, strength, unit }
      })
    }

    // Priority 2 (Fallback): If api_strengths is missing, try parsing generic_name
    if (defaultData?.generic_name) {
      return defaultData.generic_name.split('/').map(name => ({ api_name: name.trim(), strength: '', unit: '' }))
    }

    // Priority 3 (Default): If no data, provide one empty field
    return [{ api_name: '', strength: '', unit: '' }]
  }, [defaultData])

  const defaultValues = {
    batch_number: defaultData?.batch_number ?? '',
    date_of_manufacture: '',
    expiry_date: '',
    stock_location: defaultData?.stock_location ?? '',
    inventory_location: '',
    facility: '',
    nafdac_number: defaultData?.nafdac_number ?? '',
    quantity_received: Number(defaultData?.quantity_received) || 0,
    selling_price: defaultData?.selling_price || '0',
    stock_quantity: Number(defaultData?.stock_quantity) || 0,
    unit_cost: defaultData?.unit_cost || '0',
    api_strengths: parsedApiStrengths,
    pack_content_unit: defaultData?.pack_content_unit || '',
    packaging: defaultData?.packaging || '',
    pack_quantity: defaultData?.pack_quantity || '',
    inventory_unit: defaultData?.dosage_form || ''
  }
  const methods = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(productInventorySchema)
  })

  const { handleSubmit, watch, reset, formState, control } = methods
  const [step, setStep] = useState<1 | 2>(1)
  const [disableBtn, setDisableBtn] = useState<boolean>(true)
  const [showSuccess, setShowSuccess] = useState<boolean>(false)

  const { addInventory } = useProductInventory()
  const BatchNoValue = watch('batch_number')

  const value = useMemo(() => {
    if (BatchNoValue) {
      return { label: BatchNoValue, value: BatchNoValue }
    } else {
      return { label: '', value: 'BatchNoValue' }
    }
  }, [BatchNoValue])

  const watchedExpiryDate = watch('expiry_date')
  const watchedQuantityReceived = watch('quantity_received')
  const watchedUnitCostPrice = watch('unit_cost')
  const watchedUnitSellingPrice = watch('selling_price')
  const watchedPackQuantity = watch('pack_quantity')
  const watchedPackUnit = watch('pack_content_unit')

  const stepOneValid =
    !!BatchNoValue &&
    !!watchedExpiryDate &&
    !!watchedQuantityReceived &&
    !!watchedUnitCostPrice &&
    !!watchedUnitSellingPrice &&
    !!watchedPackQuantity &&
    !!watchedPackUnit

  useEffect(() => {
    if (step === 1) setDisableBtn(!stepOneValid)
    else setDisableBtn(false)
  }, [step, stepOneValid])

  const handleNextStep = () => {
    if (step === 1) setStep(2)
  }

  const handleBack = () => {
    if (step === 2) setStep(1)
    else {
      reset()
      onClose()
    }
  }

  const handleCloseModal = () => {
    reset()
    onClose()
  }

  // FIX: Updated onSubmit to correctly transform the data
  const onSubmit = async (data: InferType<typeof productInventorySchema>) => {
    const values = methods.getValues()
    const { api_strengths, pack_content_unit, facility, inventory_location, ...rest } = data

    const formattedApiStrengths = api_strengths.map((item: any) => {
      const finalStrengthString = `${item.strength}${item.unit}`.trim()
      return {
        api_name: item.api_name,
        strength: finalStrengthString
      }
    })

    const payload = {
      data: {
        attributes: {
          ...rest,
          selling_price:
            values.selling_price !== undefined
              ? String(Number(values.selling_price) / Number(values.pack_quantity))
              : undefined,
          unit_cost:
            values.unit_cost !== undefined
              ? String(Number(values.unit_cost) / Number(values.pack_quantity))
              : undefined,
          stock_quantity: Number(values.quantity_received) * Number(values.pack_quantity),
          pack_content_unit: pack_content_unit,
          strength: formattedApiStrengths
            .map((item: any) => item.strength)
            .filter(Boolean)
            .join('/')
        },
        relationships: {
          product: {
            data: {
              id: id,
              type: 'product'
            }
          },
          staff: {
            data: {
              id: `${user?.id}`,
              type: 'staff'
            }
          },
          inventory_location: {
            data: {
              id: inventory_location,
              type: 'inventory_location'
            }
          },
          facility: {
            data: {
              id: facility,
              type: 'facility'
            }
          }
        },
        type: 'product_inventories'
      }
    }

    try {
      await addInventory(payload)
      setShowSuccess(true)
    } catch (err) {
      getError(err)
    }
  }

  const submitAddStock = handleSubmit(onSubmit)

  const handlePrimaryAction = () => {
    if (step === 1) {
      handleNextStep()
      return
    }
    void submitAddStock()
  }

  const handleFormSubmit = (event: SyntheticEvent) => {
    event.preventDefault()
  }

  // Batch-specific form (single-line layout like CreateProduct)
  function renderBatchStep() {
    return (
      <Box className="!min-h-[auto]" gap="small" pad={{ bottom: '2em' }}>
        {drugName ? <Text style={{ fontSize: '14px', fontWeight: 500 }}>Drug: {drugName}</Text> : null}
        <ApiStrengthsField />

        <FormInputField label="NAFDAC Reg. No." name="nafdac_number" placeholder="NAFDAC Reg. No." />
        <div>
          <Controller
            control={methods.control}
            name="batch_number"
            render={({ field: { onChange } }) => (
              <Select
                borderRadius={'25px'}
                defaultOptions
                getOptionValue={({ value }) => value}
                isAsync
                isCreatable
                label="Batch Number"
                loadOptions={() => inventoryAttributeAction(id || '', 'batch_number')}
                onChange={(val: any) => onChange(val?.value)}
                value={value}
              />
            )}
          />
        </div>
        <Box>
          <Text style={{ fontSize: '14px', fontWeight: 500 }}>Date of Manufacture</Text>
          <Controller
            control={control}
            name="date_of_manufacture"
            render={({ field }) => (
              <Input
                handleChange={e => field.onChange(e.target?.value)}
                placeholder="Select manufacture date"
                type="date"
                value={field?.value || ''}
              />
            )}
          />
        </Box>

        <Box>
          <Text style={{ fontSize: '14px', fontWeight: 500 }}>Expiry Date</Text>
          <Controller
            control={control}
            name="expiry_date"
            render={({ field }) => (
              <Input
                handleChange={e => field.onChange(e.target?.value)}
                placeholder="Select expiry date"
                type="date"
                value={field?.value || ''}
              />
            )}
          />
        </Box>
        <FormSelect
          borderRadius="100px"
          getOptionLabel={(o: { label: string }) => o.label}
          getOptionValue={(o: { label: string; value: string }) => o.value}
          label="Unit of Measurement (UoM)"
          name="inventory_unit"
          options={inventoryUnitOptions}
          placeholder="E.g. mg, ml"
        />
        <FormSelect
          borderRadius="100px"
          formatCreateLabel={(inputValue: string) => `Add "${inputValue}"`}
          getOptionLabel={(o: { label: string }) => o.label}
          getOptionValue={(o: { label: string; value: string }) => o.value}
          isCreatable
          label="Physical Packaging"
          name="packaging"
          options={packagingOptions}
          placeholder="Bottles, Blister packs..."
        />
        <Box className="!min-h-[auto]">
          <Text margin={{ bottom: '2px' }} style={{ fontSize: '14px', fontWeight: 500 }}>
            Unit Pack Quantity/Size <span style={{ color: '#de2c21' }}>*</span>
          </Text>
          <Box
            align="center"
            border={{
              color: formState.errors.pack_quantity || formState.errors.pack_content_unit ? '#de2c21' : '#999999',
              side: 'all'
            }}
            direction="row"
            round={'100px'}
            width={'100%'}
          >
            <Controller
              control={methods.control}
              name="pack_quantity"
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter Quantity/Size"
                  style={{ border: '0', height: '100%', padding: '', width: '250px' }}
                />
              )}
            />

            <Box className="borderless !items-center !border-none" direction="row">
              <div className="borderless h-8"></div>
              <Controller
                control={control}
                name="pack_content_unit"
                render={({ field }) => (
                  <SearchInput
                    dropProps={{}}
                    onChange={val => field.onChange(val?.value || '')}
                    options={[...unit_of_measurement, 'ltr', 'kg', 'mg', 'mcg']}
                    placeholder="unit"
                    setValue={() => {}}
                    style={{ border: 'none', outline: 0, padding: '', textTransform: 'lowercase' }}
                    value={field?.value || ''}
                  />
                )}
              />
            </Box>
          </Box>
          {(formState.errors.pack_quantity || formState.errors.pack_content_unit) && (
            <Text size="small" style={{ color: '#de2c21', marginTop: '4px' }}>
              {formState.errors.pack_quantity?.message || formState.errors.pack_content_unit?.message}
            </Text>
          )}
        </Box>
        <FormInputField
          label={`Quantity Received `}
          min={1}
          name="quantity_received"
          placeholder="Enter quantity received"
          type="number"
        />
        <FormInputField label="Unit Cost Price" min={0} name="unit_cost" placeholder="Enter unit cost" type="number" />
        <FormInputField
          label="Unit Selling Price"
          min={0}
          name="selling_price"
          placeholder="Enter selling price"
          type="number"
        />
      </Box>
    )
  }

  function renderLocationStep() {
    const { setValue } = methods
    return (
      <Box className="!min-h-[auto]" gap="small">
        <Controller
          control={control}
          name="stock_location"
          render={({ field }) => (
            <Select
              borderRadius="100px"
              defaultOptions
              getOptionValue={({ value }) => value}
              isAsync
              loadOptions={(inputValue: string) => loadInventoryLocationOptions(inputValue, true)}
              onChange={(val: any) => {
                field.onChange(val?.value)
                setValue('facility', val?.facilityId || '')
                setValue('inventory_location', val?.inventoryLocationId || '')
              }}
              placeholder="Select location"
            />
          )}
        />
      </Box>
    )
  }

  return (
    <FormProvider {...methods}>
      {!showSuccess ? (
        <Modal onClose={onClose} reset={reset} title="Add New Stock">
          <Form onSubmit={handleFormSubmit}>
            <Box
              className="!min-h-[auto]"
              flex={false}
              pad={{ horizontal: '32px', vertical: '16px' }}
              style={{
                overflowX: 'hidden',
                overflowY: 'auto'
              }}
              width={'100%'}
            >
              <Box align="center" direction="row" gap="16px" margin={{ bottom: '32px' }}>
                <FormStep check={step > 1} label="Batch Information" step={1} />
                <Divider style={{ maxWidth: '48px' }} />
                <FormStep check={step === 2} label="Location & Stock Update" step={2} />
              </Box>
              <Box
                style={{
                  minHeight: '320px',
                  overflowX: step !== 1 ? 'hidden' : undefined,
                  overflowY: step !== 1 ? 'auto' : undefined,
                  position: 'relative'
                }}
              >
                <div
                  className={`step-slide ${step === 1 ? 'active' : ''}`}
                  style={{
                    position: 'absolute',
                    transform: `translateX(${step === 1 ? '0' : '-100%'})`,
                    transition: 'all 0.3s ease-in-out',
                    width: '100%'
                  }}
                >
                  {renderBatchStep()}
                </div>
                <div
                  className={`step-slide ${step === 2 ? 'active' : ''}`}
                  style={{
                    position: 'absolute',
                    transform: `translateX(${step === 2 ? '0' : step < 2 ? '100%' : '-100%'})`,
                    transition: 'all 0.3s ease-in-out',
                    width: '100%'
                  }}
                >
                  {renderLocationStep()}
                </div>
              </Box>
            </Box>
            <Box style={{ background: '#fff', bottom: 0, position: 'sticky', zIndex: 20 }}>
              <FormButton
                cancel={step === 1 ? 'Cancel' : 'Previous'}
                disabled={!stepOneValid}
                isLoading={formState.isSubmitting}
                leftIcon={step === 1 ? undefined : <FaArrowLeft />}
                onClick={handlePrimaryAction}
                onClose={handleBack}
                submit={step === 2 ? 'Add Stock' : 'Proceed'}
                type="button"
              />
            </Box>
          </Form>
        </Modal>
      ) : (
        <ProductSuccess
          description={'New stock has been added successfully.'}
          onClick={() => {
            setShowSuccess(false)
            openModal(PHARMACY_MODAL_TYPE.ADD_STOCK, { defaultData, id })
          }}
          onClose={handleCloseModal}
          rBtnLabel={'Add another Stock'}
          title={'Stock Added!'}
        />
      )}
    </FormProvider>
  )
}
